import {createRule, type Maybe} from '@regle/core';
import {isFilled} from '@regle/rules';
import {useTranslation} from '#imports';

const COUNTRIES_WITH_MANDATORY_REGION: string[] = ['CA', 'MX', 'US'] as const;

export function isRegionMandatoryForCountry(countryCode?: string): boolean {
    return !!countryCode && COUNTRIES_WITH_MANDATORY_REGION.includes(countryCode);
}

/**
 * Check if the region is required for the country provided
 *
 * @param countryCode the country you want to check
 *
 * @example
 * ```ts
 *  import { useRegle, regionRequiredIf } from '@malt/forms';
 *
 *  const { r$ } = useRegle(... , {
 *    region: { required: regionRequiredIf(countryCode) }
 *  })
 * ```
 * */
export const regionRequiredIf = createRule({
    type: 'required',
    validator(value: Maybe<string>, countryCode?: string) {
        if (isRegionMandatoryForCountry(countryCode)) {
            return isFilled(value);
        }
        return true;
    },
    active({$params: [countryCode]}) {
        return isRegionMandatoryForCountry(countryCode);
    },
    message: () => {
        const {t} = useTranslation();
        return t('generic.forms.validation.errors.required');
    },
});
